import styled, { keyframes } from 'styled-components';
import { Icon } from 'components/Icon';
import { row } from 'styles/globalStyles';
import { padStart } from 'lodash';
import * as dayjs from 'dayjs';
import { useRef } from 'react';
import weatherPm10Service from 'services/weather/weather-pm10-service';

const SkeletonTodayInfo = function() {
  return (
    <TodayInfoStyle>
      <div className="card">
        <div className="skeleton skeleton-icon"></div>
        <div className="today_txt">
          <div className="skeleton skeleton-temp"></div>
          <div className="row">
            <div className="skeleton skeleton-desc"></div>
          </div>
        </div>
      </div>
      <div className="bg">
        <div className="row">
          <div className="skeleton skeleton-data"></div>
          <span className="line" />
          <div className="skeleton skeleton-data"></div>
          <span className="line" />
          <div className="skeleton skeleton-data"></div>
        </div>
        <div className="row">
          <div className="skeleton skeleton-data"></div>
          <span className="line" />
          <div className="skeleton skeleton-data"></div>
          <span className="line" />
          <div className="skeleton skeleton-data"></div>
        </div>
      </div>
      <div className="row dust">
        <div className="bg row">
          <div className="skeleton skeleton-dust-icon"></div>
          <div>
            <div className="skeleton skeleton-dust-text"></div>
            <div className="skeleton skeleton-tag"></div>
          </div>
        </div>
        <div className="bg row">
          <div className="skeleton skeleton-dust-icon"></div>
          <div>
            <div className="skeleton skeleton-dust-text"></div>
            <div className="skeleton skeleton-tag"></div>
          </div>
        </div>
      </div>
    </TodayInfoStyle>
  );
};

const ErrorTodayInfo = function({ onRetry }) {
  return (
    <TodayInfoStyle>
      <ErrorContainer>
        <Icon name="Error" $width={48} $height={48} />
        <p className="error-message">날씨 정보를 불러오는 데 문제가 발생했습니다.</p>
        <RetryButton onClick={onRetry}>
          <Icon name="Refresh" $width={16} $height={16} />
          <span>다시 시도</span>
        </RetryButton>
      </ErrorContainer>
    </TodayInfoStyle>
  );
};

const temperatureCompareIcon = (yTemp) => {
  let parsed = 0;

  try {
    parsed = parseFloat(yTemp);
  } catch (e) {
    console.error(e);
  }

  if (parsed > 0) {
    return <Icon name="Up" $padding="5px 0" />
  } else if (parsed < 0) {
    return <Icon name="Down" $padding="5px 0" />
  } else {
    return null;
  }
};

export function TodayInfo({loading = false, data, error, onRetry}) {
  const monthRef = useRef(dayjs().month);

  if (loading) {
    return <SkeletonTodayInfo />;
  }

  if (error || !data) {
    return <ErrorTodayInfo onRetry={onRetry} />;
  }

  return (
    <TodayInfoStyle>
      <div className="card">
        <Icon name={`Weather${padStart(data.wetrCd, 2, '0')}`} $width={70} $height={70} />
        <div className="today_txt">
          <p className="txt_1">{data.tmpr && parseFloat(data.tmpr).toFixed(1)}°</p>
          <div className="row">
            <p className="txt_2">{data.wetrTxt}</p>
            <span className="line" />
            {
              data.yTemp &&
              <>
                <p className="txt_3">어제보다 {data.yTemp}°</p>
                {temperatureCompareIcon(data.yTemp)}
              </>
            }
          </div>
        </div>
      </div>
      <div className="bg">
        <div className="row">
          <p>
            습도
            <span className="bold"> {data.humd}</span>
            <span className="unit">%</span>
          </p>
          <span className="line" />
          {
            (monthRef.current >=4 && monthRef.current <= 9) ?
            <p>
              불쾌지수
              <span className="bold"> {data.disIdx}</span>
              <span className="unit"></span>
            </p>
            :
            <p>
              체감
              <span className="bold"> {(data.sTmpr && `${parseFloat(data.sTmpr).toFixed(1)}`)}</span>
              <span className="unit">°</span>
            </p>
          }
          <span className="line" />
          <p>
            남서풍
            <span className="bold"> {data.windSpd}</span>
            <span className="unit">m/s</span>
          </p>
        </div>
        <div className="row">
          <p>
            강수량
            <span className="bold"> {data.onehourRainAmt}</span>
            <span className="unit">mm</span>
          </p>
          <span className="line" />
          <p>
            일출
            <span className="bold"> {data.sunRise}</span>
          </p>
          <span className="line" />
          <p>
            일몰<span className="bold"> {data.sunSet}</span>
          </p>
        </div>
      </div>
      <div className="row dust">
        <div className="bg row">
          <Icon name="FineDust" $width={48} $height={48} />
          <div>
            <p className="fs_12 fw_500">미세먼지</p>
            <Tag className={weatherPm10Service.getPm10Type(data.pm10).class}>{weatherPm10Service.getPm10Type(data.pm10).text}</Tag>
          </div>
        </div>
        <div className="bg row">
          <Icon name="UltrafineDust" $width={48} $height={48} />
          <div>
            <p className="fs_12 fw_500">초미세먼지</p>
            <Tag className={weatherPm10Service.getPm25Type(data.pm25).class}>{weatherPm10Service.getPm25Type(data.pm25).text}</Tag>
          </div>
        </div>
      </div>
    </TodayInfoStyle>
  );
}

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const TodayInfoStyle = styled.div`
  padding-top: 16px;

  // Skeleton styles
  .skeleton {
    background: #eee;
    background-image: linear-gradient(
      to right,
      #eee 0%,
      #f5f5f5 20%,
      #eee 40%,
      #eee 100%
    );
    background-repeat: no-repeat;
    background-size: 200px 100%;
    animation: ${shimmer} 1.5s infinite;
    border-radius: 4px;
  }
  
  .skeleton-icon {
    width: 70px;
    height: 70px;
    border-radius: 50%;
  }
  
  .skeleton-temp {
    width: 90px;
    height: 38px;
    margin-bottom: 8px;
  }
  
  .skeleton-desc {
    width: 140px;
    height: 20px;
  }
  
  .skeleton-data {
    width: 80px;
    height: 20px;
  }
  
  .skeleton-dust-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
  }
  
  .skeleton-dust-text {
    width: 60px;
    height: 15px;
    margin-bottom: 5px;
  }
  
  .skeleton-tag {
    width: 50px;
    height: 22px;
    border-radius: 100px;
  }

  .card {
    ${row}
    border: 1px solid #e4e7ea;
    border-radius: 10px;
    padding: 16px 16px 16px 10%;
    gap: 8px;
  }

  .row {
    ${row}
    align-items: center;
  }

  .today_txt {
    padding-left: 16px;
  }

  .txt_1 {
    font-size: 38px;
    font-weight: 700;
    font-size: 1.2;
  }

  .txt_2 {
    color: #46494b;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.5;
  }

  .txt_3 {
    color: #46494b;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.5;
  }

  .line {
    width: 1px;
    height: 10px;
    display: inline-block;
    background-color: #7a7d7f;
    margin: 0 8px;
  }

  & > .bg {
    margin: 8px 0;
    align-items: center;
    justify-content: center;

    & > div {
      justify-content: center;
    }

    p {
      color: #252729;
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .dust {
    justify-content: cneter;
    gap: 8px;

    & > div {
      flex: 5;
      gap: 8px;
      justify-content: space-between;

      div {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
    }
  }

  .bg {
    background-color: #f9fbff;
    border-radius: 10px;
    padding: 16px;

    .line {
      background-color: #e4e7ea;
    }

    .unit {
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
    }

    .bold {
      font-weight: 700;
    }

    .fs_12 {
      font-size: 12px;
      line-height: 22px;
      text-align: center;
    }

    .fw_500 {
      font-weight: 500;
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background-color: #f9fbff;
  border-radius: 10px;
  gap: 16px;
  text-align: center;

  .error-message {
    color: #e74c3c;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const RetryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #e74c3c;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #c0392b;
  }

  &:active {
    background-color: #c0392b;
  }
`;

const Tag = styled.div`
  padding: 4px 8px;
  color: white;
  border-radius: 100px;
  font-size: 14px;
  line-height: 1;
  width: fit-content;

  &.normal {
    background-color: #34c759;
  }
  &.bad {
    background-color: #ec9418;
  }
  &.very_bad {
    background-color: #ff3b30;
  }
  &.good {
    background-color: #32ade6;
  }
`;